import type { NativeBannerSize, NativeBannerSlot, NativeBannerVariant, PageAdType } from '@/modules/adv/types'

const MREC: NativeBannerSize[] = [[300, 250]]
const LEADERBOARD: NativeBannerSize[] = [[728, 90]]
const STICKY: NativeBannerSize[] = [[320, 50]]
const STICKY_TABLET: NativeBannerSize[] = [[320, 90]]
const STICKY_TABLET_WIDE: NativeBannerSize[] = [[728, 90]]

const TABLET_STICKY: NativeBannerVariant[] = [
    { minViewportWidth: 728, sizes: STICKY_TABLET_WIDE },
    { sizes: STICKY_TABLET },
]

const UNIVERSAL: NativeBannerSlot = {
    sizes: [...MREC, ...STICKY_TABLET_WIDE, ...STICKY_TABLET, ...STICKY],
    coordinates: 'page',
    adaptiveSize: true,
}

const UNIVERSAL_1_AD_UNIT_DONORS: Record<string, PageAdType> = {
    '300x250': 'universal_banner_1_mrec',
    '320x50': 'universal_banner_1_banner',
    '320x90': 'universal_banner_1_banner',
    '728x90': 'universal_banner_1_banner',
}

const AD_UNIT_DONORS: Partial<Record<PageAdType, Record<string, PageAdType>>> = {
    universal_banner_1: UNIVERSAL_1_AD_UNIT_DONORS,
    universal_banner_short_1: UNIVERSAL_1_AD_UNIT_DONORS,
    universal_banner_2: {
        '300x250': 'universal_banner_2_mrec',
        '320x50': 'universal_banner_2_banner',
        '320x90': 'universal_banner_2_banner',
        '728x90': 'universal_banner_2_banner',
    },
}

const NATIVE_BANNER_SLOTS: Partial<Record<PageAdType, NativeBannerSlot>> = {
    sidebar: { sizes: MREC, coordinates: 'page' },
    sidebar_bottom: { sizes: MREC, coordinates: 'page' },
    sidebar_fullscreen: { sizes: MREC, coordinates: 'page' },
    sidebar_bottom_fullscreen: { sizes: MREC, coordinates: 'page' },
    leaderboard: { sizes: LEADERBOARD, coordinates: 'page' },
    leaderboard_rest: { sizes: LEADERBOARD, coordinates: 'page' },
    widget_sticky_mobile: { sizes: STICKY, coordinates: 'page' },
    sticky_tablet: { sizes: STICKY_TABLET, coordinates: 'page' },
    widget_sticky_tablet: { sizes: STICKY_TABLET, coordinates: 'page' },
    universal_banner_1: UNIVERSAL,
    universal_banner_2: UNIVERSAL,
    universal_banner_short_1: UNIVERSAL,
    sticky_mobile: { sizes: STICKY, tablet: TABLET_STICKY, coordinates: 'screen' },
    sticky_mobile_non_game_page: { sizes: STICKY, tablet: TABLET_STICKY, coordinates: 'screen' },
}

export function resolveNativeBannerSizes(slot: NativeBannerSlot, viewportWidth: number): NativeBannerSize[] {
    const variant = slot.tablet?.find(({ minViewportWidth = 0 }) => viewportWidth >= minViewportWidth)
    return variant?.sizes ?? slot.sizes
}

export function pickAdaptiveBannerSize(
    sizes: NativeBannerSize[],
    box: { width: number; height: number },
): NativeBannerSize | undefined {
    return sizes
        .filter(([width, height]) => box.width >= width && box.height >= height)
        .sort(([aWidth, aHeight], [bWidth, bHeight]) => bWidth * bHeight - aWidth * aHeight)[0]
}

export function getNativeAdUnitDonor(placement: PageAdType, sizes: NativeBannerSize[]): PageAdType {
    const size = sizes[0]

    if (!NATIVE_BANNER_SLOTS[placement]?.adaptiveSize || !size) {
        return placement
    }

    return AD_UNIT_DONORS[placement]?.[`${size[0]}x${size[1]}`] ?? placement
}

export type Viewport = { width: number; height: number }

const TABLET_MIN_VIEWPORT_SIDE = 480

function isTabletViewport({ width, height }: Viewport): boolean {
    return width > TABLET_MIN_VIEWPORT_SIDE && height > TABLET_MIN_VIEWPORT_SIDE
}

export function getNativeBannerSlot(placement: PageAdType, viewport: Viewport): NativeBannerSlot | undefined {
    const slot = NATIVE_BANNER_SLOTS[placement]

    if (!slot?.tablet) {
        return slot
    }

    if (!isTabletViewport(viewport)) {
        return { ...slot, tablet: undefined }
    }

    return { ...slot, sizes: resolveNativeBannerSizes(slot, viewport.width) }
}
